import axios from "axios";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { useGlobalMessagesStore } from "./globalMessagesStore";
import { AuditDocument, AuditReviewDocument, PagedList, UploadDocumentRequest } from "@/audit.types";
import { handleError } from "./storeHelper";
import { BTableOptions, serializeSortBy } from "@/components/BTable/BTable.types";

import { BFieldSelection, BFilterableField, BFilterOptions, dateTimeOperations, listOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import { serializeFilters } from "@/components/BTable/HelperFunctions";

export const useAuditDocumentStore = defineStore("auditDocumentStore", () => {
    const loading = ref<boolean>(false);
    const items = ref<AuditDocument[]>();
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = 'api/v1/audit/documents';

    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [{key: "name", isDescending: false}],
        filterBy: [],
        pagingMode: "withTotal"
    } as BTableOptions);

    const statuses: BFieldSelection[] = [
        { text: "Requested", value: "DN" },
        { text: "Uploaded", value: "DU" },
        { text: "Accepted", value: "DA" },
        { text: "Rejected", value: "DR" }];

    const filterableFields = reactive<BFilterableField[]>([
        {
            property: "name",
            label: "Name",
            operations: textOperations,
            editorComponent: "text-editor",
        },       
        {
            property: "status",
            label: "Status",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: statuses
        },
    ]);

    const list = async (auditId: number) => {
        try {
            loading.value = true;
            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<AuditDocument>>(`${apiUrl}/${auditId}`, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                }
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;

        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const create = async (item: AuditDocument): Promise<AuditDocument> => {
        try {
            loading.value = true;
            const response = await axios.put<AuditDocument>(apiUrl, item);
            globalMessages.push({ message: "Document created successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const deleteItem = async (id: number): Promise<void> => {
        try {
            loading.value = true;
            const response = await axios.delete<AuditDocument>(`${apiUrl}/${id}`);
            globalMessages.push({ message: "Document deleted successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const upload = async (uploadDocumentRequest: UploadDocumentRequest): Promise<void> => {

        try {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', uploadDocumentRequest.file);
            await axios.post<AuditDocument>(`${apiUrl}/actions/${uploadDocumentRequest.id}/upload`, formData);
            globalMessages.push({ message: "Document uploaded successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const review = async (review: AuditReviewDocument): Promise<void> => {
        try {
            loading.value = true;
            await axios.post<AuditDocument>(`${apiUrl}/actions/${review.id}/review`, review);
            globalMessages.push({ message: "Document reviewed successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }


    return {
        tableOptions,
        filterOptions,
        filterableFields,
        loading,
        create,
        deleteItem,
        items,
        upload,
        review,
        list
    };
})